import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Save, Loader2, X, Plus } from 'lucide-react';
import { createEmailTemplate, updateEmailTemplate } from '@/lib/api';
import type { EmailTemplate } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface EmailTemplateEditorProps {
  template?: EmailTemplate;
  onSave: () => void;
  onCancel: () => void;
}

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'recruitment', label: 'Recruitment' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'rejection', label: 'Rejection' },
];

export function EmailTemplateEditor({ template, onSave, onCancel }: EmailTemplateEditorProps) {
  const { addToast } = useToast();
  const isEditing = !!template;

  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [category, setCategory] = useState(template?.category ?? 'general');
  const [variables, setVariables] = useState<string[]>(template?.variables ?? []);
  const [variableInput, setVariableInput] = useState('');

  useEffect(() => {
    if (template) {
      setName(template.name);
      setSubject(template.subject);
      setBody(template.body);
      setCategory(template.category ?? 'general');
      setVariables(template.variables ?? []);
    }
  }, [template]);

  const createMutation = useMutation({
    mutationFn: (data: { name: string; subject: string; body: string; category: string; variables: string[] }) =>
      createEmailTemplate(data),
    onSuccess: () => {
      addToast({
        type: 'success',
        title: 'Template created',
        message: `Template "${name}" has been created successfully.`,
      });
      onSave();
    },
    onError: (error: Error) => {
      addToast({
        type: 'error',
        title: 'Failed to create template',
        message: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name: string; subject: string; body: string; category: string; variables: string[] }) =>
      updateEmailTemplate(template!.id, data),
    onSuccess: () => {
      addToast({
        type: 'success',
        title: 'Template updated',
        message: `Template "${name}" has been updated successfully.`,
      });
      onSave();
    },
    onError: (error: Error) => {
      addToast({
        type: 'error',
        title: 'Failed to update template',
        message: error.message,
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleAddVariable() {
    const trimmed = variableInput.trim();
    if (trimmed && !variables.includes(trimmed)) {
      setVariables([...variables, trimmed]);
      setVariableInput('');
    }
  }

  function handleVariableKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddVariable();
    }
  }

  function removeVariable(variable: string) {
    setVariables(variables.filter((v) => v !== variable));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !subject.trim() || !body.trim()) return;

    const payload = {
      name: name.trim(),
      subject: subject.trim(),
      body: body.trim(),
      category,
      variables,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  }

  const canSubmit = name.trim() && subject.trim() && body.trim() && !isPending;

  return (
    <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
      <h3 className="text-lg font-semibold text-text-primary mb-4">
        {isEditing ? 'Edit Template' : 'Create Template'}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div className="space-y-1.5">
          <Label className="text-text-secondary">Template Name</Label>
          <Input
            placeholder="e.g., Interview Invitation"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        {/* Category */}
        <div className="space-y-1.5">
          <Label className="text-text-secondary">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Subject */}
        <div className="space-y-1.5">
          <Label className="text-text-secondary">Subject Line</Label>
          <Input
            placeholder="Email subject line"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
        </div>

        {/* Body */}
        <div className="space-y-1.5">
          <Label className="text-text-secondary">Body</Label>
          <Textarea
            placeholder="Write your template body. Use {{variable_name}} for dynamic content."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            required
            className="min-h-[200px] resize-y font-mono text-sm"
          />
        </div>

        {/* Variables */}
        <div className="space-y-1.5">
          <Label className="text-text-secondary">Template Variables</Label>
          <p className="text-xs text-text-tertiary">
            Define variables that can be replaced when using this template (e.g., candidate_name, job_title).
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="Add a variable name"
              value={variableInput}
              onChange={(e) => setVariableInput(e.target.value)}
              onKeyDown={handleVariableKeyDown}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleAddVariable}
              disabled={!variableInput.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {variables.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {variables.map((variable) => (
                <span
                  key={variable}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/10 text-xs text-text-primary font-mono"
                >
                  {`{{${variable}}}`}
                  <button
                    type="button"
                    onClick={() => removeVariable(variable)}
                    className="text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </div>
      </form>
    </div>
  );
}
